import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { useLocation } from 'react-router';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useNavigate } from '@/hooks/use-navigate';
import { useAuth } from '@/providers/auth';
import { useCheckAndExecutePendingAction } from '@/hooks/check-and-execute-pending-action';
import { ACTION_PARAMS, getSearchWithoutActionParams } from '@/hooks/use-filters-panel-state';
import { resolvePendingWishlistProduct } from '@/lib/pending-wishlist-product';
import {
    WISHLIST_LOGIN_DRAWER_ID,
    WISHLIST_SELECTION_DRAWER_ID,
} from '@/components/wishlist/wishlist-selection-drawer-a11y';
import {
    WishlistOverlayContext,
    type LoginDrawerRequest,
    type WishlistBulkSaveRequest,
    type WishlistOverlayTrigger,
    type WishlistSelectionRequest,
} from '@/providers/wishlist-overlay-context';

const LoginDrawer = lazy(() => import('@/components/login/login-drawer'));
const WishlistSelectionDrawer = lazy(() =>
    import('@/components/wishlist/wishlist-selection-drawer').then((module) => ({
        default: module.WishlistSelectionDrawer,
    }))
);
const WishlistBulkSaveDrawer = lazy(() =>
    import('@/components/wishlist/wishlist-bulk-save-drawer').then((module) => ({
        default: module.WishlistBulkSaveDrawer,
    }))
);

function getCleanCurrentPath(): string {
    if (typeof window === 'undefined') {
        return '/';
    }
    const url = new URL(window.location.href);
    for (const key of ACTION_PARAMS) {
        url.searchParams.delete(key);
    }
    return url.pathname + url.search;
}

function parseProductIds(raw: unknown): string[] {
    if (typeof raw !== 'string' || !raw.trim()) {
        return [];
    }
    try {
        const parsed = JSON.parse(raw) as unknown;
        return Array.isArray(parsed)
            ? parsed.filter((id): id is string => typeof id === 'string' && id.length > 0)
            : [];
    } catch {
        return [];
    }
}

/**
 * Hosts a single LoginDrawer and WishlistSelectionDrawer for PLP tiles and other product grids.
 * Pending addToWishlist actions after login are handled here (not per tile).
 */
export default function WishlistOverlayProvider({ children }: PropsWithChildren) {
    const config = useConfig();
    const location = useLocation();
    const navigate = useNavigate();
    const session = useAuth();
    const isAuthenticated = Boolean(session?.userType === 'registered' && session?.customerId);

    const [loginLoaded, setLoginLoaded] = useState(false);
    const [loginOpen, setLoginOpen] = useState(false);
    const [loginRequest, setLoginRequest] = useState<LoginDrawerRequest>({});

    const [wishlistLoaded, setWishlistLoaded] = useState(false);
    const [wishlistOpen, setWishlistOpen] = useState(false);
    const [wishlistRequest, setWishlistRequest] = useState<WishlistSelectionRequest | null>(null);

    const [bulkWishlistLoaded, setBulkWishlistLoaded] = useState(false);
    const [bulkWishlistOpen, setBulkWishlistOpen] = useState(false);
    const [bulkWishlistRequest, setBulkWishlistRequest] = useState<WishlistBulkSaveRequest | null>(null);

    const closeLoginDrawer = useCallback(() => {
        setLoginOpen(false);
    }, []);

    const openLoginDrawer = useCallback((request: LoginDrawerRequest) => {
        setLoginLoaded(true);
        setLoginRequest(request);
        setLoginOpen(true);
    }, []);

    // Login from the drawer uses a React Router redirect (not a full reload), so loginOpen
    // must be cleared when the session becomes registered.
    useEffect(() => {
        if (isAuthenticated && loginOpen) {
            closeLoginDrawer();
        }
    }, [isAuthenticated, loginOpen, closeLoginDrawer]);

    const openWishlistSelectionDrawer = useCallback((request: WishlistSelectionRequest) => {
        setWishlistLoaded(true);
        setWishlistRequest(request);
        setWishlistOpen(true);
    }, []);

    const openWishlistBulkSaveDrawer = useCallback((request: WishlistBulkSaveRequest) => {
        setBulkWishlistLoaded(true);
        setBulkWishlistRequest(request);
        setBulkWishlistOpen(true);
    }, []);

    const handleWishlistOpenChange = useCallback((open: boolean) => {
        setWishlistOpen(open);
        if (!open) {
            setWishlistRequest(null);
        }
    }, []);

    const handleBulkWishlistOpenChange = useCallback((open: boolean) => {
        setBulkWishlistOpen(open);
        if (!open) {
            setBulkWishlistRequest(null);
        }
    }, []);

    const handlePendingAddToWishlist = useCallback(
        (params: Record<string, unknown>) => {
            closeLoginDrawer();
            const { product, variant } = resolvePendingWishlistProduct(params);
            openWishlistSelectionDrawer({ product, variant });
            void navigate(
                { pathname: location.pathname, search: getSearchWithoutActionParams(location.search) },
                { replace: true }
            );
        },
        [closeLoginDrawer, openWishlistSelectionDrawer, navigate, location.pathname, location.search]
    );

    const handlePendingBulkSaveToWishlist = useCallback(
        (params: Record<string, unknown>) => {
            closeLoginDrawer();
            const productIds = parseProductIds(params.productIds);
            if (productIds.length > 0) {
                openWishlistBulkSaveDrawer({ productIds });
            }
            void navigate(
                { pathname: location.pathname, search: getSearchWithoutActionParams(location.search) },
                { replace: true }
            );
        },
        [closeLoginDrawer, openWishlistBulkSaveDrawer, navigate, location.pathname, location.search]
    );

    useCheckAndExecutePendingAction({
        actionName: 'addToWishlist',
        shouldExecute: (params) => isAuthenticated && Boolean(params.productId),
        onMatch: handlePendingAddToWishlist,
    });

    useCheckAndExecutePendingAction({
        actionName: 'bulkSaveToWishlist',
        shouldExecute: (params) => isAuthenticated && parseProductIds(params.productIds).length > 0,
        onMatch: handlePendingBulkSaveToWishlist,
    });

    const overlayTriggerElementRef = useRef<HTMLElement | null>(null);
    const [activeTriggerId, setActiveTriggerId] = useState<string>();

    const setOverlayTrigger = useCallback((trigger: WishlistOverlayTrigger) => {
        overlayTriggerElementRef.current = trigger.element;
        setActiveTriggerId(trigger.id);
    }, []);

    const anyOverlayOpen = loginOpen || wishlistOpen || bulkWishlistOpen;
    const anyOverlayOpenRef = useRef(anyOverlayOpen);
    useEffect(() => {
        anyOverlayOpenRef.current = anyOverlayOpen;
    }, [anyOverlayOpen]);

    // Radix modal dialogs restore focus only to a `Dialog.Trigger`; these drawers are opened programmatically.
    const handleOverlayCloseAutoFocus = useCallback((event: Event) => {
        if (anyOverlayOpenRef.current) {
            // Handoff (e.g. login drawer → wishlist drawer): keep the trigger for the next overlay.
            event.preventDefault();
            return;
        }
        const trigger = overlayTriggerElementRef.current;
        overlayTriggerElementRef.current = null;
        setActiveTriggerId(undefined);
        if (trigger?.isConnected) {
            event.preventDefault();
            trigger.focus();
        }
    }, []);

    const openOverlayId = wishlistOpen
        ? WISHLIST_SELECTION_DRAWER_ID
        : loginOpen
          ? WISHLIST_LOGIN_DRAWER_ID
          : undefined;

    const value = useMemo(
        () => ({
            openLoginDrawer,
            openWishlistSelectionDrawer,
            openWishlistBulkSaveDrawer,
            setOverlayTrigger,
            activeTriggerId,
            openOverlayId,
        }),
        [
            openLoginDrawer,
            openWishlistSelectionDrawer,
            openWishlistBulkSaveDrawer,
            setOverlayTrigger,
            activeTriggerId,
            openOverlayId,
        ]
    );

    return (
        <WishlistOverlayContext.Provider value={value}>
            {children}
            {loginLoaded ? (
                <Suspense fallback={null}>
                    <LoginDrawer
                        id={WISHLIST_LOGIN_DRAWER_ID}
                        onCloseAutoFocus={handleOverlayCloseAutoFocus}
                        isOpen={loginOpen}
                        onOpenChange={setLoginOpen}
                        onSuccess={closeLoginDrawer}
                        isSocialLoginEnabled={Boolean(config.features.socialLogin?.enabled)}
                        returnUrl={loginRequest.returnUrl ?? getCleanCurrentPath()}
                        action={loginRequest.action}
                        actionParams={loginRequest.actionParams}
                    />
                </Suspense>
            ) : null}
            {wishlistLoaded && wishlistRequest ? (
                <Suspense fallback={null}>
                    <WishlistSelectionDrawer
                        open={wishlistOpen}
                        onOpenChange={handleWishlistOpenChange}
                        product={wishlistRequest.product}
                        variant={wishlistRequest.variant}
                        contentId={WISHLIST_SELECTION_DRAWER_ID}
                        onCloseAutoFocus={handleOverlayCloseAutoFocus}
                    />
                </Suspense>
            ) : null}
            {bulkWishlistLoaded && bulkWishlistRequest ? (
                <Suspense fallback={null}>
                    <WishlistBulkSaveDrawer
                        open={bulkWishlistOpen}
                        onOpenChange={handleBulkWishlistOpenChange}
                        productIds={bulkWishlistRequest.productIds}
                    />
                </Suspense>
            ) : null}
        </WishlistOverlayContext.Provider>
    );
}
